import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Header,
  Icon,
  Input,
  Message,
  Modal,
  Select,
  Segment,
  Table,
  Tab,
  Label,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';

const Reseller = () => {
  const { t } = useTranslation();
  const [resellerInfo, setResellerInfo] = useState(null);
  const [subAgents, setSubAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  // 代理定价相关状态
  const [channels, setChannels] = useState([]);
  const [ratioList, setRatioList] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [ratioModalOpen, setRatioModalOpen] = useState(false);
  const [editingRatio, setEditingRatio] = useState(null);
  const [ratioForm, setRatioForm] = useState({ channel_id: '', ratio: '' });

  useEffect(() => {
    fetchResellerInfo();
    fetchSubAgents();
    fetchPricingChannels();
    fetchRatioList();
  }, []);

  const fetchResellerInfo = async () => {
    try {
      const res = await API.get('/api/reseller/info');
      const { success, data } = res.data;
      if (success) {
        setResellerInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch reseller info:', err);
    }
  };

  const fetchSubAgents = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/reseller/sub-agents');
      const { success, data } = res.data;
      if (success) {
        setSubAgents(data || []);
      } else {
        setSubAgents([]);
      }
    } catch (err) {
      console.error('Failed to fetch sub-agents:', err);
      setSubAgents([]);
    }
    setLoading(false);
  };

  const getInviteLink = () => {
    if (!resellerInfo?.aff_code) return '';
    return `${window.location.origin}/register?aff=${resellerInfo.aff_code}`;
  };

  const copyInviteLink = () => {
    const link = getInviteLink();
    navigator.clipboard.writeText(link).then(() => {
      alert(t('reseller.link_copied'));
    });
  };

  const panes = [
    {
      menuItem: t('reseller.tab.my_info'),
      render: () => (
        <Tab.Pane>
          <Header as='h3'>{t('reseller.my_info')}</Header>
          
          {resellerInfo?.reseller_parent_id > 0 ? (
            <Message info>
              <Message.Header>{t('reseller.sub_agent_title')}</Message.Header>
              <p>{t('reseller.parent_agent')}: <strong>{resellerInfo.parent_display_name || resellerInfo.parent_username}</strong></p>
              <p>{t('reseller.sub_agent_desc')}</p>
            </Message>
          ) : resellerInfo?.is_reseller ? (
            <>
              <Message success>
                <Message.Header>{t('reseller.level1_agent_title')}</Message.Header>
                <p>{t('reseller.level1_agent_desc')}</p>
              </Message>
              
              <Segment>
                <Header as='h4'>{t('reseller.invite_link')}</Header>
                <p>{t('reseller.invite_link_desc')}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                  <code style={{ 
                    flex: 1, 
                    padding: '10px', 
                    background: '#f5f5f5', 
                    borderRadius: '4px',
                    wordBreak: 'break-all',
                  }}>
                    {getInviteLink()}
                  </code>
                  <Button primary size='small' onClick={copyInviteLink}>
                    <Icon name='copy' />
                    {t('reseller.copy_link')}
                  </Button>
                </div>
                <p style={{ marginTop: '10px' }}>
                  {t('reseller.aff_code')}: <Label>{resellerInfo.aff_code}</Label>
                </p>
              </Segment>
            </>
          ) : (
            <Message warning>
              <Message.Header>{t('reseller.not_reseller')}</Message.Header>
              <p>{t('reseller.not_reseller_desc')}</p>
            </Message>
          )}
        </Tab.Pane>
      ),
    },
  ];

  // ====== 代理定价相关函数 ======
  const fetchPricingChannels = async () => {
    try {
      const res = await API.get('/api/reseller/pricing/channels');
      const { success, data } = res.data;
      if (success) {
        setChannels((data || []).map(ch => ({
          key: ch.id,
          value: ch.id,
          text: `${ch.name} (ID: ${ch.id})`,
        })));
      }
    } catch (err) {
      console.error('Failed to fetch pricing channels:', err);
    }
  };

  const fetchRatioList = async () => {
    setPricingLoading(true);
    try {
      const res = await API.get('/api/reseller/pricing/ratios');
      const { success, data } = res.data;
      if (success) {
        setRatioList(data || []);
      } else {
        setRatioList([]);
      }
    } catch (err) {
      showError(t('reseller.pricing.fetch_failed'));
      setRatioList([]);
    }
    setPricingLoading(false);
  };

  const openRatioModal = (ratio = null) => {
    if (ratio) {
      setEditingRatio(ratio);
      setRatioForm({ channel_id: ratio.channel_id, ratio: ratio.ratio });
    } else {
      setEditingRatio(null);
      setRatioForm({ channel_id: '', ratio: '' });
    }
    setRatioModalOpen(true);
  };

  const closeRatioModal = () => {
    setRatioModalOpen(false);
    setEditingRatio(null);
    setRatioForm({ channel_id: '', ratio: '' });
  };

  const saveRatio = async () => {
    if (!ratioForm.channel_id) {
      showError(t('reseller.pricing.select_channel'));
      return;
    }
    const ratioVal = parseFloat(ratioForm.ratio);
    if (!ratioVal || ratioVal <= 0) {
      showError(t('reseller.pricing.ratio_placeholder'));
      return;
    }
    setPricingLoading(true);
    try {
      const res = await API.post('/api/reseller/pricing/ratio', {
        channel_id: parseInt(ratioForm.channel_id),
        ratio: ratioVal,
      });
      if (res.data.success) {
        showSuccess(t('reseller.pricing.save_success'));
        closeRatioModal();
        fetchRatioList();
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(t('reseller.pricing.fetch_failed'));
    }
    setPricingLoading(false);
  };

  const deleteRatio = async (channelId) => {
    if (!window.confirm(t('reseller.pricing.confirm_delete'))) return;
    setPricingLoading(true);
    try {
      const res = await API.delete(`/api/reseller/pricing/ratio/${channelId}`);
      if (res.data.success) {
        showSuccess(t('reseller.pricing.delete_success'));
        fetchRatioList();
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(t('reseller.pricing.fetch_failed'));
    }
    setPricingLoading(false);
  };

  // 只有一级代理才显示“分销商客户”和“代理定价”Tab
  if (resellerInfo?.is_reseller) {
    panes.push({
      menuItem: t('reseller.tab.sub_agents'),
      render: () => (
        <Tab.Pane loading={loading}>
          <Header as='h3'>
            {t('reseller.sub_agents_list')}
            {subAgents.length > 0 && <Label circular size='small' style={{ marginLeft: '8px' }}>{subAgents.length}</Label>}
          </Header>
          
          {subAgents.length === 0 ? (
            <Message info>
              <Message.Header>{t('reseller.no_sub_agents')}</Message.Header>
              <p>{t('reseller.no_sub_agents_desc')}</p>
            </Message>
          ) : (
            <Table celled striped selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t('reseller.col.username')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('reseller.col.display_name')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('reseller.col.status')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('reseller.col.used_quota')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('reseller.col.request_count')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {subAgents.map((agent) => (
                  <Table.Row key={agent.id}>
                    <Table.Cell>
                      <Label>{agent.username}</Label>
                    </Table.Cell>
                    <Table.Cell>{agent.display_name || '-'}</Table.Cell>
                    <Table.Cell>
                      {agent.status === 1 ? (
                        <Label color='green' size='tiny'>{t('reseller.status_enabled')}</Label>
                      ) : (
                        <Label color='red' size='tiny'>{t('reseller.status_disabled')}</Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>{(agent.used_quota || 0).toFixed(4)}</Table.Cell>
                    <Table.Cell>{agent.request_count || 0}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Tab.Pane>
      ),
    });

    // 代理定价 Tab
    panes.push({
      menuItem: t('reseller.pricing.tab'),
      render: () => (
        <Tab.Pane loading={pricingLoading}>
          <Header as='h3'>
            {t('reseller.pricing.title')}
            <Header.Subheader>{t('reseller.pricing.subtitle')}</Header.Subheader>
          </Header>

          <Button
            primary
            icon
            labelPosition='left'
            onClick={() => openRatioModal()}
            style={{ marginBottom: '1em' }}
          >
            <Icon name='plus' />
            {t('reseller.pricing.add_ratio')}
          </Button>

          {ratioList.length === 0 ? (
            <Message info>
              <Message.Header>{t('reseller.pricing.no_ratios')}</Message.Header>
              <p>{t('reseller.pricing.no_ratios_desc')}</p>
            </Message>
          ) : (
            <Table celled striped selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t('reseller.pricing.col.channel')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('reseller.pricing.col.fenxiao_ratio', '成本倍率')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('reseller.pricing.col.ratio')}</Table.HeaderCell>
                  <Table.HeaderCell textAlign='center'>{t('reseller.pricing.col.actions')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {ratioList.map((item) => (
                  <Table.Row key={item.channel_id}>
                    <Table.Cell>
                      <Label>{item.channel_name || `ID: ${item.channel_id}`}</Label>
                    </Table.Cell>
                    <Table.Cell style={{ color: '#999' }}>{item.fenxiao_ratio}</Table.Cell>
                    <Table.Cell>{item.ratio}</Table.Cell>
                    <Table.Cell textAlign='center'>
                      <Button icon size='mini' onClick={() => openRatioModal(item)}>
                        <Icon name='edit' />
                      </Button>
                      <Button icon size='mini' color='red' onClick={() => deleteRatio(item.channel_id)}>
                        <Icon name='trash' />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {/* 倍率弹窗 */}
          <Modal open={ratioModalOpen} onClose={closeRatioModal} size='small'>
            <Modal.Header>
              {editingRatio ? t('reseller.pricing.edit_ratio') : t('reseller.pricing.add_ratio')}
            </Modal.Header>
            <Modal.Content>
              <Form>
                <Form.Field required>
                  <label>{t('reseller.pricing.select_channel')}</label>
                  <Select
                    placeholder={t('reseller.pricing.select_channel_placeholder')}
                    options={channels}
                    value={ratioForm.channel_id}
                    onChange={(e, { value }) => setRatioForm({ ...ratioForm, channel_id: value })}
                    search
                    selection
                    disabled={!!editingRatio}
                  />
                </Form.Field>
                <Form.Field required>
                  <label>{t('reseller.pricing.ratio')}</label>
                  <Input
                    type='number'
                    step='0.0001'
                    placeholder={t('reseller.pricing.ratio_placeholder')}
                    value={ratioForm.ratio}
                    onChange={(e) => setRatioForm({ ...ratioForm, ratio: e.target.value })}
                  />
                </Form.Field>
              </Form>
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={closeRatioModal}>取消</Button>
              <Button primary onClick={saveRatio} loading={pricingLoading}>
                保存
              </Button>
            </Modal.Actions>
          </Modal>
        </Tab.Pane>
      ),
    });
  }

  return (
    <div className='dashboard-container'>
      <Header as='h2'>
        <Icon name='users' />
        <Header.Content>
          {t('reseller.title')}
          <Header.Subheader>{t('reseller.subtitle')}</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Tab 
          menu={{ secondary: true, pointing: true }}
          panes={panes}
        />
      </Segment>
    </div>
  );
};

export default Reseller;
